import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Box,
  CardActions,
  IconButton,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  Chat as ChatIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { REGISTER_DISABLED } from '../config';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const features = [
    {
      icon: <ChatIcon sx={{ fontSize: 40 }} />,
      title: 'Smart Chatbots',
      description: 'AI-powered chatbots that understand and respond to customer queries naturally.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with 99.9% uptime guarantee.',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: 'Lightning Fast',
      description: 'Instant responses and seamless integration with your website.',
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40 }} />,
      title: '24/7 Support',
      description: 'Round-the-clock customer support and technical assistance.',
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Header />
      
      {/* Hero Section */}
      <Box
        sx={{ 
          background: theme.palette.mode === 'dark' 
            ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          py: 12,
          textAlign: 'center',
        }}
      >
        <Container>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Transform Your Customer Service
          </Typography>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 4 }}>
            AI-powered chatbots that engage customers and boost your business
          </Typography>
          {REGISTER_DISABLED ? (
            <Tooltip title="Registration is closed right now">
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  disabled
                  sx={{ 
                    mr: 2,
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  Get Started
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/register')}
              sx={{ 
                mr: 2,
                bgcolor: 'white',
                color: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              Get Started
            </Button>
          )}
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            onClick={() => navigate('/showcase')}
          >
            Explore Features
          </Button>
        </Container>
      </Box>

      {/* Features Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 6 }}>
          Why Choose Glowspirit?
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <IconButton color="primary" onClick={() => navigate('/help')}>
                    <ArrowForwardIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
        py: 8,
        borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      }}>
        <Container>
          <Box sx={{ textAlign: 'center', maxWidth: 700, mx: 'auto' }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                mb: 2,
                fontWeight: 600,
                color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
              }}
            >
              Ready to Get Started?
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4,
                color: theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary',
                fontWeight: 400
              }}
            >
              Join growing businesses improving customer service with Glowspirit
            </Typography>
            
            {REGISTER_DISABLED ? (
              <Tooltip title="Registration is closed right now">
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled
                    sx={{ mt: 2 }}
                  >
                    Create Your Account
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{ 
                  mt: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '1.1rem',
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'primary.main' : 'primary.main',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.dark',
                    transform: 'translateY(-2px)',
                    transition: 'transform 0.2s',
                    boxShadow: theme.palette.mode === 'dark' ? '0 6px 20px rgba(0,0,0,0.4)' : '0 6px 16px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.2s',
                  boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.25)' : '0 4px 8px rgba(0,0,0,0.15)',
                }}
              >
                Create Your Account
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default LandingPage; 